/**
 * PWA Install Banner Component
 * Shows a custom install prompt with role-based messaging
 */

import React from 'react';
import { X, Download, Share, Plus } from 'lucide-react';
import { useInstallPrompt } from './installPrompt';

export default function InstallBanner({ userRole = 'learner', theme = 'dark' }) {
  const {
    showPrompt,
    isIOS,
    isInstalled,
    canInstall,
    installApp,
    dismiss,
    message
  } = useInstallPrompt(userRole);

  if (!showPrompt || isInstalled) return null;

  const isDark = theme === 'dark';

  return (
    <div
      className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 rounded-2xl shadow-2xl border transition-all duration-300 animate-slide-up ${
        isDark
          ? 'bg-[#0a1628] border-gray-700 text-white'
          : 'bg-white border-gray-200 text-gray-900'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between p-4 pb-2">
        <div className="flex items-center gap-3">
          <div className="text-2xl">{message.icon}</div>
          <div>
            <h3 className="font-bold text-sm">{message.title}</h3>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {isIOS ? 'Add to Home Screen' : 'Install App'}
            </p>
          </div>
        </div>
        <button
          onClick={() => dismiss(true)}
          className={`p-1.5 rounded-lg transition-colors ${
            isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}
        >
          <X size={16} className="opacity-50" />
        </button>
      </div>

      {/* Message */}
      <p className={`px-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
        {message.message}
      </p>

      {/* iOS Instructions or Install Button */}
      {isIOS ? (
        <div className={`mx-4 my-4 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
          <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            To install on iOS:
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
              }`}>
                1
              </div>
              <span>Tap the <Share size={14} className="inline mx-1" /> Share button</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
              }`}>
                2
              </div>
              <span>Scroll down and tap <Plus size={14} className="inline mx-1" /> "Add to Home Screen"</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 flex gap-2">
          <button
            onClick={installApp}
            disabled={!canInstall}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold hover:from-blue-500 hover:to-indigo-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={16} />
            Install Now
          </button>
          <button
            onClick={() => dismiss(true)}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isDark
                ? 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Later
          </button>
        </div>
      )}

      {/* Dismiss permanently option */}
      <div className={`px-4 pb-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
        <button
          onClick={() => dismiss(false)}
          className={`w-full text-center text-xs py-2 transition-colors ${
            isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Don't show again
        </button>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
